import { toaster } from '@optiaxiom/react';

import http from '@frontend/api/http';
import { HYPATIA_URL } from '@frontend/envConfig';
import {
  Trigger,
  WorkflowAgent,
  WorkflowAgentExecution,
  WorkflowAgentTriggerResponse,
} from '@frontend/types/workflow-agent';

import { createEmailTrigger } from '../components/workflow-agent/services/email-trigger';
import { convertToWorkflowAgent } from '../components/workflow-agent/services/graph-to-workflow';
import { createMessageTrigger } from '../components/workflow-agent/services/message-trigger';
import { createScheduledTrigger } from '../components/workflow-agent/services/scheduled-trigger';
import { createWebhookTrigger } from '../components/workflow-agent/services/webhook-trigger';

export const createWorkflowTriggers = async (
  instanceId: string,
  workflowId: string,
  triggers: Trigger[]
) => {
  const triggerPromises: Array<{ promise: Promise<unknown>; type: string }> = [];

  // Create a mapping of trigger types to their creation functions
  const triggerCreators = {
    webhook: createWebhookTrigger,
    email: createEmailTrigger,
    scheduled: createScheduledTrigger,
    message: createMessageTrigger,
  } as const;

  // Loop through all triggers and push to triggerPromises based on type
  triggers.forEach(trigger => {
    if (trigger.type === 'webhook') {
      triggerPromises.push({
        promise: triggerCreators.webhook(
          workflowId,
          instanceId,
          trigger as unknown as Record<string, unknown>
        ),
        type: trigger.type,
      });
    } else if (trigger.type === 'email') {
      triggerPromises.push({
        promise: triggerCreators.email(
          workflowId,
          instanceId,
          trigger as unknown as Record<string, unknown>
        ),
        type: trigger.type,
      });
    } else if (trigger.type === 'scheduled') {
      triggerPromises.push({
        promise: triggerCreators.scheduled(
          workflowId,
          instanceId,
          trigger as unknown as Record<string, unknown>
        ),
        type: trigger.type,
      });
    } else if (trigger.type === 'message') {
      triggerPromises.push({
        promise: triggerCreators.message(
          workflowId,
          instanceId,
          trigger as unknown as Record<string, unknown>
        ),
        type: trigger.type,
      });
    } else {
      console.warn(`No creator function found for trigger type: ${(trigger as Trigger).type}`);
    }
  });

  if (triggerPromises.length > 0) {
    const results = await Promise.allSettled(triggerPromises.map(item => item.promise));

    results.forEach((result, index) => {
      const triggerType = triggerPromises[index].type;
      if (result.status === 'rejected') {
        console.error(`Failed to create ${triggerType} trigger:`, result.reason);
        toaster.create(`Failed to create ${triggerType} trigger`, { intent: 'danger' });
      }
    });
  }
};

export const createWorkflowAgent = async (
  agent: WorkflowAgent,
  instanceId: string
): Promise<WorkflowAgent> => {
  const data = convertToWorkflowAgent(agent.nodes || [], agent.edges || [], agent);

  try {
    const response = await http.post<WorkflowAgent>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows`,
      data.agentData
    );
    return response.data;
  } catch (error) {
    console.error('Failed to save workflow agent:', error);
    throw new Error('Failed to save workflow agent');
  }
};

export const updateWorkflowAgent = async (
  agent: WorkflowAgent,
  updateUrl: string
): Promise<WorkflowAgent> => {
  try {
    const { agentData, triggers } = convertToWorkflowAgent(
      agent.nodes || [],
      agent.edges || [],
      agent
    );

    const updatePayload = {
      ...agentData,
      triggers,
    };

    const response = await http.put<WorkflowAgent>(updateUrl, updatePayload);
    return response.data;
  } catch (error) {
    console.error('Failed to update workflow agent:', error);
    throw new Error('Failed to update workflow agent');
  }
};

export const getWorkflowAgents = (
  instanceId: string,
  page = 1,
  pageSize = 1000,
  includeDeleted = false,
  includeSteps = false
) => ({
  key: () =>
    ['opal:workflow-agents', instanceId, page, pageSize, includeDeleted, includeSteps] as const,
  queryFn: () => async (): Promise<WorkflowAgent[]> => {
    const response = await http.get<{ items: WorkflowAgent[] }>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows?page=${page}&page_size=${pageSize}&include_deleted=${includeDeleted}&include_steps=${includeSteps}`
    );
    return response.data.items || [];
  },
});

export const getWorkflowAgent = (url: string) => async (): Promise<WorkflowAgent> => {
  const response = await http.get<WorkflowAgent>(url);
  return response.data;
};

export const deleteWorkflowAgent = async (deleteUrl: string): Promise<void> => {
  try {
    await http.delete(deleteUrl);
    return;
  } catch (error) {
    console.error('Failed to delete workflow agent:', error);
    throw new Error('Failed to delete workflow agent');
  }
};

export const triggerWorkflowAgent = async (
  instanceId: string,
  agentId: string,
  inputText: string
): Promise<WorkflowAgentTriggerResponse> => {
  try {
    const response = await http.post<WorkflowAgentTriggerResponse>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/${agentId}/trigger`,
      inputText,
      {
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to trigger workflow agent:', error);
    throw new Error('Failed to trigger workflow agent');
  }
};

export const getWorkflowAgentExecution = async (
  instanceId: string,
  executionId: string
): Promise<WorkflowAgentExecution> => {
  const response = await http.get<WorkflowAgentExecution>(
    `${HYPATIA_URL}/api/v1/${instanceId}/workflow-executions/${executionId}`
  );
  return response.data;
};

export const exportWorkflowAgent = async (instanceId: string, agentId: string) => {
  const response = await http.get<{ data: unknown }>(
    `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/${agentId}/export`
  );
  return response.data.data;
};

export const importWorkflowAgent = async (
  instanceId: string,
  agentData: object,
  mode: 'skip' | 'overwrite' = 'overwrite'
) => {
  const res = await http.post(
    `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/import?mode=${mode}`,
    agentData
  );
  return res.data;
};
